export class UserSettingsStore {
    static readonly CURRENT_SCHEMA_VERSION = 1;

    static readonly Keys = {
        SchemaVersion: 'wecloud.settings.schemaVersion',
        RotationMode: 'wecloud.rotation.mode',
        SmoothRotationSpeed: 'wecloud.rotation.smoothSpeed',
        SnapRotationAngle: 'wecloud.rotation.snapAngle',
        AudioTuning: 'wecloud.audioTuning',
        EventsUsername: 'events-username',
        EventsAvatar: 'events-avatar',
        EventsShareMic: 'events-share-mic',
        EventsHeadlessMode: 'events-headless-mode',
        EventsRoom: 'events-room',
        EventsAvatarId: 'events-avatar-id',
        IntifaceUrl: 'intiface-url',
    } as const;

    private static initialized = false;

    static initialize(): void {
        if (this.initialized) {
            return;
        }

        const storedVersion = this.getNumberRaw(this.Keys.SchemaVersion);
        if (storedVersion == null) {
            this.setNumberRaw(this.Keys.SchemaVersion, this.CURRENT_SCHEMA_VERSION);
            this.initialized = true;
            return;
        }

        if (storedVersion < this.CURRENT_SCHEMA_VERSION) {
            this.runMigrations(storedVersion, this.CURRENT_SCHEMA_VERSION);
            this.setNumberRaw(this.Keys.SchemaVersion, this.CURRENT_SCHEMA_VERSION);
        }

        this.initialized = true;
    }

    private static runMigrations(fromVersion: number, toVersion: number): void {
        for (let version = fromVersion + 1; version <= toVersion; version++) {
            switch (version) {
                case 1:
                    break;
            }
        }
    }

    private static getStringRaw(key: string): string | null {
        if (typeof localStorage === 'undefined') {
            return null;
        }
        try {
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private static setStringRaw(key: string, value: string): void {
        if (typeof localStorage === 'undefined') {
            return;
        }
        try {
            localStorage.setItem(key, value);
        } catch {
            // ignore storage write failures
        }
    }

    private static getNumberRaw(key: string): number | null {
        const value = this.getStringRaw(key);
        if (value == null) {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private static setNumberRaw(key: string, value: number): void {
        if (!Number.isFinite(value)) {
            return;
        }
        this.setStringRaw(key, String(value));
    }

    static getString(key: string): string | null {
        this.initialize();
        return this.getStringRaw(key);
    }

    static getRotationMode(defaultMode: 'smooth' | 'snap' = 'snap'): 'smooth' | 'snap' {
        this.initialize();

        const mode = this.getStringRaw(this.Keys.RotationMode);
        if (mode === 'smooth' || mode === 'snap') {
            return mode;
        }

        this.setStringRaw(this.Keys.RotationMode, defaultMode);
        return defaultMode;
    }

    static setString(key: string, value: string): void {
        this.initialize();
        this.setStringRaw(key, value);
    }

    static getNumber(key: string): number | null {
        this.initialize();
        return this.getNumberRaw(key);
    }

    static setNumber(key: string, value: number): void {
        this.initialize();
        this.setNumberRaw(key, value);
    }

    static getJson<T>(key: string): T | null {
        this.initialize();
        const value = this.getString(key);
        if (value == null) {
            return null;
        }
        try {
            return JSON.parse(value) as T;
        } catch {
            return null;
        }
    }

    static setJson<T>(key: string, value: T): void {
        this.initialize();
        this.setString(key, JSON.stringify(value));
    }

    static getCookie(name: string): string {
        if (typeof document === 'undefined') {
            return '';
        }
        const target = `${name}=`;
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
            cookie = cookie.trim();
            if (cookie.startsWith(target)) {
                return cookie.substring(target.length);
            }
        }
        return '';
    }

    static setCookie(name: string, value: string, expiresDays: number): void {
        if (typeof document === 'undefined') {
            return;
        }
        const expiresDate = new Date();
        expiresDate.setTime(expiresDate.getTime() + expiresDays * 24 * 60 * 60 * 1000);
        const expires = `expires=${expiresDate.toUTCString()}`;
        document.cookie = `${name}=${value};${expires};path=/`;
    }

    static getKnownPersistedSettings(): Array<{ key: string; storage: 'localStorage' | 'cookie'; value: string }> {
        this.initialize();

        const localStorageKeys = [
            this.Keys.SchemaVersion,
            this.Keys.RotationMode,
            this.Keys.SmoothRotationSpeed,
            this.Keys.SnapRotationAngle,
            this.Keys.AudioTuning,
        ];

        const cookieKeys = [
            this.Keys.EventsUsername,
            this.Keys.EventsAvatar,
            this.Keys.EventsAvatarId,
            this.Keys.EventsShareMic,
            this.Keys.EventsHeadlessMode,
            this.Keys.EventsRoom,
            this.Keys.IntifaceUrl,
        ];

        const entries: Array<{ key: string; storage: 'localStorage' | 'cookie'; value: string }> = [];

        for (const key of localStorageKeys) {
            const value = this.getStringRaw(key);
            entries.push({ key, storage: 'localStorage', value: value ?? '' });
        }

        for (const key of cookieKeys) {
            const value = this.getCookie(key);
            entries.push({ key, storage: 'cookie', value: value ?? '' });
        }

        return entries;
    }
}
